export enum AgentType {
  LEAD_QUALIFICATION = 'lead_qualification',
  LEAD_ENRICHMENT = 'lead_enrichment',
  FOLLOW_UP_SCHEDULER = 'follow_up_scheduler',
  SENTIMENT_ANALYSIS = 'sentiment_analysis',
  PROMPT_ENRICHER = 'prompt_enricher',
  LINKEDIN_COMPANY_RESEARCH = 'linkedin_company_research',
  BROWSER_COMPANY_RESEARCH = 'browser_company_research',
  FLOWSELLAI_COMPANY_RESEARCH = 'flowsellai_company_research',
  INSTAGRAM_COMPANY_RESEARCH = 'instagram_company_research',
  ORCHESTRATOR = 'orchestrator',
}

export enum AgentExecutionStatus {
  QUEUED = 'queued',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum ModelTier {
  FAST = 'fast',
  BALANCED = 'balanced',
  PREMIUM = 'premium',
}

export type ConfidenceLevel = 'high' | 'medium' | 'low';

/**
 * Whether a run actually used the model. A rule-based fallback is stored as
 * `status: 'completed'`, so this is the only field that distinguishes real AI
 * output from degraded output (FLOSEAI-610).
 */
export type AgentExecutionOutcome = 'success' | 'fallback' | 'failed';

export interface AgentExecution {
  id: string;
  organizationId: string;
  agentType: AgentType;
  status: AgentExecutionStatus;
  outcome?: AgentExecutionOutcome | null;
  inputTokens: number;
  outputTokens: number;
  modelUsed: string | null;
  costUsd: string;
  durationMs: number | null;
  leadId: string | null;
  triggeredBy: string | null;
  errorMessage: string | null;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  metadata: Record<string, unknown> & { toolName?: string };
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  parentExecutionId?: string | null;
  _count?: {
    children: number;
  };
  lead?: {
    id: string;
    fullName: string;
    email: string;
  };
}

export interface AgentConfig {
  id: string;
  organizationId: string;
  enabledAgents: string[];
  modelTier: ModelTier;
  customPrompts: Record<string, string>;
  monthlyTokenLimit: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentUsageSummary {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  executionCount: number;
  planUsage: PlanUsage;
  breakdown: UsageBreakdownEntry[];
}

export interface UsageBreakdownEntry {
  agentType: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  executionCount: number;
}

export type PlanUsageLevel = 'ok' | 'warning' | 'critical';

export interface PlanUsage {
  percentUsed: number;
  level: PlanUsageLevel;
  periodEnd: string;
  warningThreshold: number;
}

export interface AgentStatusEvent {
  executionId: string;
  agentType: string;
  status: string;
  timestamp: string;
  data?: Record<string, unknown>;
}

export interface QueueStatus {
  queueSize: number;
  isProcessing: boolean;
}

export interface ExecuteAgentInput {
  organizationId: string;
  agentType: AgentType;
  leadId?: string;
  input?: Record<string, unknown>;
}

export interface ExecuteAgentResponse {
  executionId: string;
  status: string;
  agentType: string;
}

export interface UpdateAgentConfigInput {
  organizationId: string;
  enabledAgents?: string[];
  modelTier?: ModelTier;
  customPrompts?: Record<string, string>;
  monthlyTokenLimit?: number;
  isActive?: boolean;
}

export interface QueryExecutionsParams {
  organizationId: string;
  agentType?: string;
  status?: string;
  leadId?: string;
  limit?: number;
  offset?: number;
  topLevel?: boolean;
}

export interface PaginatedExecutions {
  executions: AgentExecution[];
  total: number;
  limit: number;
  offset: number;
}

export interface CostBreakdown {
  organizationId: string;
  period: { month: number; year: number };
  totalCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  executionCount: number;
  byModel: Array<{
    model: string;
    costUsd: number;
    inputTokens: number;
    outputTokens: number;
    executionCount: number;
  }>;
  byAgentType: Array<{
    agentType: string;
    costUsd: number;
    inputTokens: number;
    outputTokens: number;
    executionCount: number;
  }>;
  dailyCosts: Array<{
    date: string;
    costUsd: number;
    executionCount: number;
  }>;
}

export interface CacheStats {
  totalHits: number;
  totalMisses: number;
  hitRate: number;
  entryCount: number;
  estimatedSavingsUsd: number;
}

export interface CommsCostBreakdown {
  totalCostUsd: number;
  byChannel: Record<string, number>;
  byType: Record<string, number>;
  messageCount: number;
}

export interface CommsCostDashboard {
  period: { start: string; end: string };
  monthly: CommsCostBreakdown;
  weekly: CommsCostBreakdown;
  budget: BudgetStatus | null;
  activeConversationWindows: number;
  queuedMessages: number;
  estimatedSavingsFromFreeWindows: number;
}

export interface BudgetStatus {
  configured: boolean;
  monthlyLimitUsd?: number;
  currentSpendUsd?: number;
  remainingUsd?: number;
  usagePercent?: number;
  isOverBudget?: boolean;
  isNearLimit?: boolean;
  message?: string;
}

export interface AgentChatMessage {
  id: string;
  organizationId: string;
  agentType: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  executionId: string | null;
  teamId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface SendChatMessageInput {
  organizationId: string;
  agentType: AgentType;
  content: string;
  executionId?: string;
  teamId?: string;
  language?: string;
}

export interface PaginatedChatMessages {
  messages: AgentChatMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface QueryChatMessagesParams {
  organizationId: string;
  agentType: string;
  limit?: number;
  offset?: number;
}

export interface AgentChatStreamEvent {
  messageId: string;
  agentType: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  toolActions?: ToolActionSummary[];
}

export enum SkillExecutionMode {
  PROMPT_ONLY = 'prompt_only',
  AUTO_EXECUTE = 'auto_execute',
  REQUIRE_APPROVAL = 'require_approval',
}

export enum ToolActionStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  EXECUTING = 'executing',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
  FAILED = 'failed',
}

export interface ToolActionSummary {
  toolName: string;
  success: boolean;
}

export interface AgentToolAction {
  id: string;
  executionId: string;
  organizationId: string;
  toolName: string;
  parameters: Record<string, unknown>;
  result: Record<string, unknown>;
  status: ToolActionStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedBy: string | null;
  rejectedAt: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: string;
}

export interface PaginatedToolActions {
  actions: AgentToolAction[];
  total: number;
  limit: number;
  offset: number;
}

export interface ToolActionStreamEvent {
  type: 'proposed' | 'completed' | 'rejected';
  organizationId: string;
  executionId: string;
  actionId?: string;
  toolName: string;
  parameters?: Record<string, unknown>;
  success?: boolean;
  reason?: string;
  timestamp: string;
}

export interface AvailableToolsResponse {
  tools: string[];
  defaults: string[];
}

export interface AgentSkill {
  id: string;
  name: string;
  agentType: string;
  description: string;
  promptSnippet: string;
  category: string;
  isDefault: boolean;
  sortOrder: number;
  organizationId: string | null;
  toolNames: string[];
  executionMode: SkillExecutionMode;
  maxToolCalls: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrgAgentSkill extends AgentSkill {
  enabled: boolean;
  isCustom: boolean;
}

export interface ToggleSkillInput {
  organizationId: string;
  skillId: string;
  enabled: boolean;
  userId?: string;
  executionMode?: SkillExecutionMode;
}

export type CustomInstructionsSource = 'manual' | 'ai_generated' | 'ai_enriched';

export interface UpdateCustomInstructionsInput {
  organizationId: string;
  agentType: string;
  instructions: string;
  source?: CustomInstructionsSource;
}

export type SuggestCustomInstructionsOutputLanguage = 'en' | 'es' | 'pt';

export interface SuggestCustomInstructionsInput {
  organizationId: string;
  agentType: string;
  currentText: string;
  outputLanguage?: SuggestCustomInstructionsOutputLanguage;
}

export interface SuggestCustomInstructionsResponse {
  suggestion: string;
}

export interface PromptPreview {
  agentType: string;
  prompt: string;
}

export interface CreateSkillInput {
  organizationId: string;
  name: string;
  agentType: string;
  description: string;
  promptSnippet: string;
  category: string;
  sortOrder?: number;
  toolNames?: string[];
  executionMode?: SkillExecutionMode;
  maxToolCalls?: number;
}

export interface UpdateSkillInput {
  organizationId: string;
  skillId: string;
  name?: string;
  description?: string;
  promptSnippet?: string;
  category?: string;
  sortOrder?: number;
  toolNames?: string[];
  executionMode?: SkillExecutionMode;
  maxToolCalls?: number;
}

export interface DeleteSkillInput {
  organizationId: string;
  skillId: string;
}

export interface ExecutePipelineInput {
  organizationId: string;
  leadId: string;
}

export interface QualificationResult {
  score: number;
  qualificationLevel: string;
  reasoning: string;
  suggestedActions: string[];
}

export interface EnrichmentResult {
  industry?: string;
  companySize?: string;
  jobTitle?: string;
  linkedinUrl?: string;
  website?: string;
  revenue?: string;
  location?: string;
  timezone?: string;
  technologies?: string[];
  socialProfiles?: string[];
  confidence?: number;
  reasoning?: string;
  dataSources?: string[];
  enrichedAt?: string;
  [key: string]: unknown;
}

export interface SentimentSignal {
  type: string;
  source: string;
  detail: string;
  weight: number;
}

export interface SentimentResult {
  overall: 'very_positive' | 'positive' | 'neutral' | 'negative' | 'very_negative';
  score: number;
  confidence: number;
  engagementLevel?: string;
  reasoning?: string;
  signals?: SentimentSignal[];
  riskFactors?: string[];
  opportunities?: string[];
  [key: string]: unknown;
}

export interface FollowUpRecommendation {
  type: string;
  priority?: string;
  suggestedDate?: string;
  suggestedTime?: string;
  reasoning?: string;
  subject?: string;
}

export interface FollowUpResult {
  optimalChannel?: string | null;
  urgency?: string | null;
  engagementScore?: number;
  reasoning?: string;
  recommendations?: FollowUpRecommendation[];
  generatedAt?: string;
  [key: string]: unknown;
}

export interface LeadAgentStatus {
  leadId: string;
  executions: AgentExecution[];
  latestResults: {
    qualification: QualificationResult | null;
    enrichment: EnrichmentResult | null;
    sentiment: SentimentResult | null;
    followUp: FollowUpResult | null;
  };
  pipelineRunning: boolean;
}
